import React, { useCallback, useRef } from "react";

import { Id } from "react-toastify";
import { ConfirmationModal } from "../general/ConfirmationModal";
import { useToaster } from "../general/ToastMessages/useToaster";
import { Transaction } from "../../common/types/transaction";
import {
    useHardDeleteTransaction,
    useRestoreTransaction,
    useSoftDeleteTransaction,
} from "../../api/transactionDeletion.api";
import {
    createTransactionDescription,
    SoftDeletedTransactionToast,
} from "./SoftDeletedTransactionToast";

interface Props {
    transaction: Transaction;
    onBack: () => void;
    onDeleteSuccess: () => void;
    onHide?: () => void;
    show?: boolean;
}

export const DeleteTransactionModal: React.FC<Props> = ({
    transaction,
    onBack,
    onDeleteSuccess,
    onHide,
    show,
}) => {
    const toastId = useRef<Id | null>(null);
    const hasUndone = useRef(false);
    const { toast, dismiss } = useToaster();

    const onDelete = useSoftDeleteTransaction();
    const onHardDelete = useHardDeleteTransaction();

    const restore = useRestoreTransaction();

    const hardDeleteTransaction = useCallback(async () => {
        await onHardDelete.mutateAsync(transaction.id);
    }, [onHardDelete, transaction.id]);

    const onToastHide = useCallback(async () => {
        if (!hasUndone.current) {
            await hardDeleteTransaction();
        }

        onHide?.();
    }, [hardDeleteTransaction, onHide]);

    const onUndo = useCallback(async () => {
        hasUndone.current = true;
        await restore.mutateAsync(transaction.id);
        toast(
            `Transaction ${createTransactionDescription(transaction)} has been restored`,
        );
        if (toastId.current) {
            dismiss(toastId.current);
        }
    }, [restore, transaction, toast, dismiss]);

    const deleteTransaction = async () => {
        hasUndone.current = false;
        await onDelete.mutateAsync(transaction.id);
        toastId.current = toast(
            <SoftDeletedTransactionToast
                transaction={transaction}
                undo={onUndo}
            />,
            transaction.id,
            { onClose: onToastHide },
        );
        onDeleteSuccess();
    };

    return (
        <ConfirmationModal
            title="Are you sure you want to delete this transaction?"
            question={`Transaction: ${createTransactionDescription(transaction)}`}
            no="Back"
            onReject={onBack}
            onConfirm={deleteTransaction}
            onHide={onHide}
            show={show}
        />
    );
};
